import { type LockedModuleKey } from '@diagon-alley/common';

import { BOTTOM_ITEMS, NAV_GROUPS, type MenuItem } from './sidebar';

/**
 * Which module a route belongs to, for the handful of routes that are not a sidebar item
 * themselves: deep links, detail pages under a different stem, and sunset surfaces that are
 * still reachable by URL. Everything else resolves through the nav definition, so a new
 * sidebar item needs only its own `module` field.
 */
const EXTRA_ROUTE_MODULES: Record<string, LockedModuleKey> = {
    // Alert detail / checklist lives under /alert/:id while the list is /alerts.
    '/alert': 'monitor.alerts',
    // The sunset PO Builder; Production Orders still deep-links into it.
    '/procurement-orders': 'execute.create_purchase_order',
    '/production-monitor': 'visibility.production',
    // Every brand-side B2B page sits under the B2B Customers item.
    '/b2b': 'visibility.b2b_customers',
};

const flattenItems = (items: MenuItem[]): MenuItem[] =>
    items.flatMap((item) => [item, ...flattenItems(item.children ?? [])]);

/** Route stem → module, longest stem first so `/stock-transfers/:id` beats `/`. */
const ROUTE_MODULES: Array<[stem: string, module: LockedModuleKey]> = [
    ...flattenItems([...NAV_GROUPS.flatMap((group) => group.items), ...BOTTOM_ITEMS])
        .filter((item): item is MenuItem & { module: LockedModuleKey } => !!item.module)
        .map((item): [string, LockedModuleKey] => [item.path.split('?')[0]!, item.module]),
    ...Object.entries(EXTRA_ROUTE_MODULES),
].sort(([a], [b]) => b.length - a.length);

const matchesStem = (pathname: string, stem: string): boolean =>
    pathname === stem || pathname.startsWith(`${stem}/`);

/**
 * The module a pathname belongs to, or null for a route no plan can lock (Home, login, the
 * vendor and customer portals). `/distribution-paths` does not match `/distribution`: a stem
 * matches at a segment boundary only.
 */
export const resolveModuleForPath = (pathname: string): LockedModuleKey | null => {
    const [path] = pathname.split('?');
    const hit = ROUTE_MODULES.find(([stem]) => matchesStem(path!, stem));

    return hit ? hit[1] : null;
};

/** Whether a module is outside the brand's plan. No list (still loading, or an old API) = open. */
export const isModuleLocked = (
    lockedModules: readonly string[] | null | undefined,
    module: LockedModuleKey | null | undefined,
): boolean => !!module && !!lockedModules && lockedModules.includes(module);

/** Whether a pathname is outside the brand's plan. */
export const isPathLocked = (
    lockedModules: readonly string[] | null | undefined,
    pathname: string,
): boolean => isModuleLocked(lockedModules, resolveModuleForPath(pathname));

export const PREMIUM_GROUP_LABEL = 'Premium';
export const PREMIUM_TOOLTIP = 'Premium feature — not on your plan yet. Contact Reparo to switch it on.';
